/**
 * Unified File Manager.
 *
 * Инициализируется автоматически только если на странице есть `.file-manager-component`
 * (маркер рендерится через <x-file-manager-wrapper>).
 *
 * В этом коммите (3.19a) — безопасный перенос public/js/file-manager.js в ES-module.
 * Разбивка на 3 модуля (core/ui/picker) и полная миграция fetch → apiRequest —
 * отдельным подэтапом, когда убедимся в стабильности.
 *
 * FancyBox-адаптер (file-manager-fancybox.js) и file-manager-unified.js удалены в 3.19
 * как неиспользуемые.
 */

// Legacy-защита: ранее на странице FM могли быть подвешены fancybox-обработчики.
// Здесь снимаем их, чтобы не открывался устаревший fancybox-режим.
document.addEventListener('DOMContentLoaded', function () {
    const $ = window.jQuery || window.$;
    if ($ && $.fancybox) {
        $(document).off('click', '[data-fancybox*="file-manager"]');
        $(document).off('click', '#create-folder-modal');
        $(document).off('click', '[href*="#create-folder"]');
    }
});

// File-manager modals — мигрированы на native <dialog> + dialog-utils.
// Backdrop и Esc-обработка — глобально через initDialogEnhancements в app.js,
// настраиваются data-close-on-backdrop / data-close-on-escape на самих <dialog>.
function showFileManagerModal(modalId) {
    // Закрыть все остальные модалки file-manager-а
    document.querySelectorAll('dialog.file-manager-modal[open]').forEach(d => {
        if (d.id !== modalId && typeof window.closeDialog === 'function') {
            window.closeDialog(d);
        }
    });

    const modal = document.getElementById(modalId);
    if (!modal) return;

    if (typeof window.openDialog === 'function') {
        window.openDialog(modal);
    } else if (typeof modal.showModal === 'function') {
        modal.showModal();
    }
}

function hideFileManagerModal(modalId) {
    const modal = document.getElementById(modalId);
    if (!modal) return;

    if (typeof window.closeDialog === 'function') {
        window.closeDialog(modal);
    } else if (typeof modal.close === 'function') {
        modal.close();
    }

    const form = modal.querySelector('form');
    if (form) form.reset();
}

// Alias — используется в data-fm-modal-close
function closeFileManagerModal(modalId) {
    hideFileManagerModal(modalId);
}

// Helper function to safely parse JSON response
async function safeJsonParse(response, context = 'request') {
    // Check if response is ok
    if (!response.ok) {
        const text = await response.text();
        console.error(`HTTP error ${response.status} in ${context}:`, text.substring(0, 500));
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    // Check content type
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error(`Invalid content-type in ${context}:`, contentType, '\nResponse:', text.substring(0, 500));
        throw new Error(`Expected JSON but got ${contentType || 'unknown type'}`);
    }

    // Parse JSON safely
    try {
        return await response.json();
    } catch (e) {
        console.error(`JSON parse error in ${context}:`, e.message);
        throw new Error('Invalid JSON response');
    }
}

class FileManager {
    constructor() {
        this.context = null;
        this.entityId = null;
        this.currentDirId = 0;
        this.selectedItem = null;
        this.folderTree = [];
        this.currentSorting = 'desc'; // Текущая сортировка по умолчанию
        
        this.init();
    }
    
    init() {
        // Get context from component
        const component = document.querySelector('.file-manager-component');
        if (component) {
            this.context = component.dataset.context;
            this.entityId = component.dataset.entityId;
            this.currentDirId = parseInt(component.dataset.dirId || 0);
            this.currentSorting = component.dataset.sorting || 'desc';
        }
        
        // Получаем параметры из URL
        const urlParams = new URLSearchParams(window.location.search);
        
        // Получаем текущую директорию из GET параметра
        const dirIdFromGet = urlParams.get('dirId');
        if (dirIdFromGet) {
            this.currentDirId = parseInt(dirIdFromGet);
            // Загружаем содержимое этой директории
            if (this.currentDirId > 0) {
                setTimeout(() => this.loadFolderContents(this.currentDirId), 100);
            }
        }
        
        // Получаем текущую сортировку из GET параметра
        const sortFromGet = urlParams.get('sort');
        
        if (sortFromGet && ['desc', 'asc', 'name_asc', 'name_desc', 'type', 'type_desc'].includes(sortFromGet)) {
            this.currentSorting = sortFromGet;
        } else {
            // Если нет GET параметра, пробуем получить из URL пути (для обратной совместимости)
            const urlParts = window.location.pathname.split('/');
            const lastPart = urlParts[urlParts.length - 1];
            if (['desc', 'asc', 'name_asc', 'name_desc', 'type', 'type_desc'].includes(lastPart)) {
                this.currentSorting = lastPart;
            }
        }
        
        // Восстанавливаем сохраненный вид из localStorage
        this.restoreSavedView();
        
        this.bindEvents();
    }
    
    restoreSavedView() {
        const viewsContainer = document.querySelector('.adm_files_views');
        if (!viewsContainer) return;
        
        // Проверяем GET-параметр
        const urlParams = new URLSearchParams(window.location.search);
        const urlView = urlParams.get('view');
        
        if (urlView) {
            // Если есть GET-параметр, сохраняем его в localStorage для будущих переходов
            localStorage.setItem('fileManagerView', urlView);
        }
        // Больше НЕ применяем localStorage автоматически
        // Вид устанавливается только через GET-параметр или остается по умолчанию (grid)
        // PHP уже установил правильные классы на основе GET-параметра
    }
    
    bindEvents() {
        // Bind sorting buttons
        this.bindSortingButtons();
        
        // Bind upload events
        this.bindUploadEvents();
        
        // View toggle
        const viewToggle = document.querySelector('.view-toggle, #adm_files_view_changer');
        if (viewToggle) {
            viewToggle.addEventListener('click', (e) => {
                e.preventDefault();
                this.toggleView();
            });
        }
        
        // Create folder button
        const createFolderBtn = document.querySelector('.fm-create-folder, .adm_files_create_folder');
        if (createFolderBtn) {
            createFolderBtn.addEventListener('click', (e) => {
                e.preventDefault();
                showFileManagerModal('fileManagerFolderModal');
            });
        }
        
        // Menu toggles
        document.querySelectorAll('.menu-toggle').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                const menu = btn.nextElementSibling;
                
                // Close other menus
                document.querySelectorAll('.menu-dropdown').forEach(m => {
                    if (m !== menu) m.classList.remove('show');
                });
                
                menu.classList.toggle('show');
            });
        });
        
        // Close menus on outside click
        document.addEventListener('click', () => {
            document.querySelectorAll('.menu-dropdown').forEach(menu => {
                menu.classList.remove('show');
            });
        });
        
        // Item actions
        this.bindItemActions();
        
        // Form submissions
        this.bindFormSubmissions();
        
        // Folder navigation
        this.bindFolderNavigation();
    }
    
    bindItemActions() {
        // Rename - поддержка всех возможных селекторов
        document.querySelectorAll('.rename-item, .fm-rename, .adm_files_cell_actions_item[href="#rename"]').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                const item = btn.closest('[data-type]');
                const name = btn.dataset.name || item.querySelector('.adm_cell_files_name')?.textContent || '';
                const itemType = btn.dataset.type || item.dataset.type;
                const itemId = btn.dataset.id || item.dataset.id;
                this.showRenameModal(itemId, itemType, name.trim());
            });
        });
        
        // Move - поддержка всех возможных селекторов
        document.querySelectorAll('.move-item, .fm-move, .adm_files_cell_actions_item[href="#move"]').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                const item = btn.closest('[data-type]');
                const itemType = btn.dataset.type || item.dataset.type;
                const itemId = btn.dataset.id || item.dataset.id;
                this.showMoveModal(itemId, itemType);
            });
        });
        
        // Delete - поддержка всех возможных селекторов
        document.querySelectorAll('.delete-item, .fm-delete, .adm_files_cell_actions_item[href="#delete"]').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                const item = btn.closest('[data-type]');
                const name = btn.dataset.name || 
                           item.querySelector('.adm_cell_files_name')?.textContent || 
                           item.querySelector('td a')?.textContent || 'this item';
                const itemType = btn.dataset.type || item.dataset.type;
                const itemId = btn.dataset.id || item.dataset.id;
                this.showDeleteModal(itemId, itemType, name.trim());
            });
        });
    }
    
    bindFolderNavigation() {
        // Handle folder clicks
        document.querySelectorAll('.folder-link').forEach(link => {
            link.addEventListener('click', async (e) => {
                e.preventDefault();
                const folderId = link.dataset.folderId;
                const folderName = link.dataset.folderName;
                
                // Update current directory
                this.currentDirId = parseInt(folderId);
                
                // Load folder contents via AJAX
                await this.loadFolderContents(folderId);
            });
        });
        
        // Handle back button
        const backLink = document.querySelector('.back-link');
        if (backLink) {
            backLink.addEventListener('click', async (e) => {
                e.preventDefault();
                
                // Get parent ID from data attribute (set by server based on current folder's parent)
                const parentId = parseInt(backLink.dataset.folderId || 0);
                
                // Update current directory
                this.currentDirId = parentId;
                
                // Load parent folder contents
                await this.loadFolderContents(parentId);
            });
        }
    }
    
    bindSortingButtons() {
        const sortButtons = document.querySelectorAll('.adm_files_sort_item:not(.no-sort)');
        
        sortButtons.forEach(button => {
            button.addEventListener('click', async (e) => {
                e.preventDefault();
                
                let newSorting;
                const buttonText = button.textContent.trim();
                
                // Определяем тип сортировки по тексту кнопки
                if (buttonText.includes('новизне') || buttonText.includes('novelty')) {
                    // Переключаем между desc и asc для даты
                    if (this.currentSorting === 'desc') {
                        newSorting = 'asc';
                    } else if (this.currentSorting === 'asc') {
                        newSorting = 'desc';
                    } else {
                        // Если была другая сортировка, начинаем с desc
                        newSorting = 'desc';
                    }
                } else if (buttonText.includes('названию') || buttonText.includes('name')) {
                    // Переключаем между name_asc и name_desc
                    if (this.currentSorting === 'name_asc') {
                        newSorting = 'name_desc';
                    } else if (this.currentSorting === 'name_desc') {
                        newSorting = 'name_asc';
                    } else {
                        // Если была другая сортировка, начинаем с name_asc
                        newSorting = 'name_asc';
                    }
                } else if (buttonText.includes('типу') || buttonText.includes('type')) {
                    // Переключаем между type и type_desc
                    if (this.currentSorting === 'type') {
                        newSorting = 'type_desc';
                    } else if (this.currentSorting === 'type_desc') {
                        newSorting = 'type';
                    } else {
                        // Если была другая сортировка, начинаем с type
                        newSorting = 'type';
                    }
                }
                
                // Обновляем текущую сортировку
                this.currentSorting = newSorting;
                
                // Обновляем активный класс на кнопках
                sortButtons.forEach(btn => btn.classList.remove('active'));
                button.classList.add('active');
                
                // Добавляем визуальную индикацию направления сортировки
                button.setAttribute('data-sort-direction', newSorting.includes('desc') || newSorting === 'desc' ? 'desc' : 'asc');
                
                // Перезагружаем содержимое с новой сортировкой
                // Используем 0 для начальной загрузки, так как сервер сам определит правильный dirId
                const dirIdToLoad = this.currentDirId || 0;
                await this.loadFolderContents(dirIdToLoad);
                
                // Обновляем URL с GET параметром вместо изменения пути
                const url = new URL(window.location);
                url.searchParams.set('sort', newSorting);
                window.history.pushState({}, '', url);
            });
        });
    }
    
    bindUploadEvents() {
        // Upload file - поддержка всех возможных селекторов кнопок загрузки
        const uploadBtns = document.querySelectorAll('.fm-upload-btn, .add-file, [data-action="upload"], .x_btn_add_file');
        let fileInput = document.getElementById('file-manager-upload') || document.getElementById('file_to_user_files') || document.getElementById('file_to_upload');
        
        // Если input существует, заменяем его чтобы удалить старые обработчики
        if (fileInput) {
            const newInput = fileInput.cloneNode(true);
            fileInput.parentNode.replaceChild(newInput, fileInput);
            fileInput = newInput; // Обновляем ссылку на новый элемент
            
            // Добавляем обработчик изменения файла
            fileInput.addEventListener('change', (e) => {
                if (e.target.files.length > 0) {
                    this.uploadFiles(e.target.files);
                }
            });
        }
        
        // Привязываем кнопки загрузки к file input
        uploadBtns.forEach(uploadBtn => {
            // Удаляем старые обработчики чтобы избежать дублирования
            const newBtn = uploadBtn.cloneNode(true);
            uploadBtn.parentNode.replaceChild(newBtn, uploadBtn);
            
            if (newBtn && fileInput) {
                newBtn.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    fileInput.click();
                });
            }
        });
    }
    
    async loadFolderContents(folderId) {
        try {
            // Show loading indicator
            const filesContainer = document.querySelector('.adm_files_views');
            if (filesContainer) {
                filesContainer.style.opacity = '0.5';
            }
            
            // Update URL with dirId parameter
            const urlObj = new URL(window.location);
            if (folderId && folderId != 0) {
                urlObj.searchParams.set('dirId', folderId);
            } else {
                urlObj.searchParams.delete('dirId');
            }
            window.history.pushState({}, '', urlObj);
            
            // Determine the correct URL based on context
            let url;
            if (this.context === 'project') {
                // For projects, use project AJAX endpoint
                url = `/project/${this.entityId}/files/list/${folderId}/${this.currentSorting}`;
            } else {
                url = `/profile/${this.entityId}/files/list/${folderId}/${this.currentSorting}`;
            }
            
            // Fetch folder contents
            const response = await fetch(url, {
                credentials: 'same-origin',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    'Accept': 'application/json'
                }
            });

            const data = await safeJsonParse(response, 'loadFolderContents');
            
            if (data.success) {
                // Update current directory ID from server response
                if (data.currentDirId !== undefined) {
                    this.currentDirId = data.currentDirId;
                }
                
                // Update file manager content
                this.updateFileManagerContent(data.files, data.folders, data.currentDirId || folderId, data.parentId);
                
                // Update hidden inputs
                if (document.querySelector('[name="dir_id"]')) {
                    document.querySelector('[name="dir_id"]').value = data.currentDirId || folderId;
                }
                
                // Re-bind events for new elements
                this.bindItemActions();
                this.bindFolderNavigation();
                this.bindUploadEvents(); // Перепривязываем события загрузки файлов
            } else {
                console.error('Response not successful:', data);
            }
            
        } catch (error) {
            console.error('Error loading folder contents:', error);
            showToast('error', 'Error loading folder contents: ' + error.message);
        } finally {
            // Hide loading indicator
            const filesContainer = document.querySelector('.adm_files_views');
            if (filesContainer) {
                filesContainer.style.opacity = '1';
            }
        }
    }
    
    updateFileManagerContent(files, folders, currentDirId, parentId) {
        // Update back button
        const backLink = document.querySelector('.back-link');
        if (backLink) {
            backLink.dataset.folderId = parentId || 0;
            // Hide back button if we're at root (parentId is 0 or null/undefined)
            if (!parentId || parentId == 0) {
                backLink.style.display = 'none';
            } else {
                backLink.style.display = '';
            }
        }
        
        // Update grid view
        const gridView = document.querySelector('.adm_files_grid');
        if (gridView) {
            // Keep the add file button
            const addFileBtn = gridView.querySelector('.adm_files_cell:has(.adm_files_add_block)');
            let html = '';
            
            if (addFileBtn) {
                html += addFileBtn.outerHTML;
            }
            
            // Add folders
            folders.forEach(folder => {
                html += this.createFolderElement(folder);
            });
            
            // Add files
            files.forEach(file => {
                html += this.createFileElement(file);
            });
            
            gridView.innerHTML = html;
        }
        
        // Update list view
        const listView = document.querySelector('.adm_files_table');
        if (listView) {
            let html = '';
            
            // Add folders
            folders.forEach(folder => {
                html += this.createFolderRow(folder);
            });
            
            // Add files
            files.forEach(file => {
                html += this.createFileRow(file);
            });
            
            const tbody = listView.querySelector('tbody') || listView;
            tbody.innerHTML = html;
        }
    }
    
    createFolderElement(folder) {
        return `
            <div class="adm_files_cell" data-type="folder" data-id="${folder.id}">
                <div class="adm_files_cell_cover">
                    <div class="adm_files_cell_actions_menu">
                        <div class="adm_files_cell_actions_menu_handler" data-file_actions>
                            <a href="#open_actions"></a>
                        </div>
                        <div class="adm_files_cell_actions_menu_close" data-file_actions>
                            <a href="#close_actions"></a>
                        </div>
                    </div>
                    
                    <div class="adm_files_cell_actions">
                        <div class="adm_files_cell_actions_wrap">
                            <div class="adm_files_cell_actions_grid">
                                <a class="adm_files_cell_actions_item fm-rename" href="#rename"
                                   data-name="${folder.name}" data-type="dir" data-id="${folder.id}">
                                    RENAME
                                </a>
                                <a class="adm_files_cell_actions_item fm-move" href="#move"
                                   data-type="dir" data-id="${folder.id}">
                                    MOVE
                                </a>
                                <a class="adm_files_cell_actions_item fm-delete" href="#delete"
                                   data-type="dir" data-id="${folder.id}">
                                    DELETE
                                </a>
                            </div>
                        </div>
                    </div>
                    
                    <a class="files_cell_square folder-link" href="#" 
                       data-folder-id="${folder.id}" 
                       data-folder-name="${folder.name}">
                        <div class="files_cell_pic pic_bg_cover" style="background-image: url('/img/content/files/folder.svg')"></div>
                    </a>
                    
                    <div class="adm_files_cell_footer">
                        <div class="adm_files_cell_footer_info adm_files_footer_text">
                            <div class="adm_files_cell_footer_cols">
                                <div class="adm_files_cell_footer_left">
                                    <span class="adm_cell_files_name">${folder.name}</span>
                                </div>
                                <div class="adm_files_cell_footer_right">
                                    <span class="adm_cell_files_date">${new Date(folder.created_at).toLocaleDateString('ru-RU')}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        `;
    }
    
    createFileElement(file) {
        const ext = file.name.split('.').pop().toLowerCase();
        let preview = '/img/content/files/file_table.png';
        if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) {
            preview = file.url;
        } else if (ext === 'pdf') {
            preview = '/img/content/files/file_pdf.png';
        }
        
        return `
            <div class="adm_files_cell" data-type="file" data-id="${file.id}">
                <div class="adm_files_cell_cover">
                    <div class="adm_files_cell_actions_menu">
                        <div class="adm_files_cell_actions_menu_handler" data-file_actions>
                            <a href="#open_actions"></a>
                        </div>
                        <div class="adm_files_cell_actions_menu_close" data-file_actions>
                            <a href="#close_actions"></a>
                        </div>
                    </div>
                    
                    <div class="adm_files_cell_actions">
                        <div class="adm_files_cell_actions_wrap">
                            <div class="adm_files_cell_actions_grid">
                                <a class="adm_files_cell_actions_item fm-rename" href="#rename"
                                   data-name="${file.name}" data-type="file" data-id="${file.id}">
                                    RENAME
                                </a>
                                <a class="adm_files_cell_actions_item fm-move" href="#move"
                                   data-type="file" data-id="${file.id}">
                                    MOVE
                                </a>
                                <a class="adm_files_cell_actions_item fm-delete" href="#delete"
                                   data-type="file" data-id="${file.id}">
                                    DELETE
                                </a>
                            </div>
                        </div>
                    </div>
                    
                    <a class="files_cell_square" href="${file.url}" target="_blank">
                        <div class="files_cell_pic pic_bg_cover" style="background-image: url('${preview}')"></div>
                    </a>
                    
                    <div class="adm_files_cell_footer">
                        <div class="adm_files_cell_footer_info adm_files_footer_text">
                            <div class="adm_files_cell_footer_cols">
                                <div class="adm_files_cell_footer_left">
                                    <span class="adm_cell_files_name">${file.name}</span>
                                </div>
                                <div class="adm_files_cell_footer_right">
                                    <span class="adm_cell_files_date">${new Date(file.created_at).toLocaleDateString('ru-RU')}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        `;
    }
    
    createFolderRow(folder) {
        return `
            <tr data-type="folder" data-id="${folder.id}">
                <td class="td_name">
                    <p>
                        <a href="#" class="folder-link" 
                           data-folder-id="${folder.id}" 
                           data-folder-name="${folder.name}">
                            ${folder.name}
                        </a>
                    </p>
                </td>
                <td class="td_extension"><p>folder</p></td>
                <td class="td_date"><p>${new Date(folder.created_at).toLocaleDateString('ru-RU')}</p></td>
                <td class="td_tools">
                    <div class="td_tools_block">
                        <a href="#rename" class="fm-rename" data-name="${folder.name}" data-type="dir" data-id="${folder.id}">
                            Rename
                        </a>
                        <a href="#move" class="fm-move" data-type="dir" data-id="${folder.id}">
                            Move
                        </a>
                        <a href="#delete" class="fm-delete" data-type="dir" data-id="${folder.id}">
                            Delete
                        </a>
                    </div>
                </td>
            </tr>
        `;
    }
    
    createFileRow(file) {
        const ext = file.name.split('.').pop();
        const filename = file.name.substring(0, file.name.lastIndexOf('.'));
        
        return `
            <tr data-type="file" data-id="${file.id}">
                <td class="td_name">
                    <p>
                        <a href="${file.url}" target="_blank">
                            ${filename}
                        </a>
                    </p>
                </td>
                <td class="td_extension"><p>${ext}</p></td>
                <td class="td_date"><p>${new Date(file.created_at).toLocaleDateString('ru-RU')}</p></td>
                <td class="td_tools">
                    <div class="td_tools_block">
                        <a href="#rename" class="fm-rename" data-name="${file.name}" data-type="file" data-id="${file.id}">
                            Rename
                        </a>
                        <a href="#move" class="fm-move" data-type="file" data-id="${file.id}">
                            Move
                        </a>
                        <a href="#delete" class="fm-delete" data-type="file" data-id="${file.id}">
                            Delete
                        </a>
                    </div>
                </td>
            </tr>
        `;
    }
    
    bindFormSubmissions() {
        // Create folder form
        const folderForm = document.getElementById('fileManagerFolderForm');
        if (folderForm) {
            folderForm.addEventListener('submit', (e) => {
                e.preventDefault();
                this.createFolder(new FormData(folderForm));
            });
        }
        
        // Rename form
        const renameForm = document.getElementById('fileManagerRenameForm');
        if (renameForm) {
            renameForm.addEventListener('submit', (e) => {
                e.preventDefault();
                this.renameItem(new FormData(renameForm));
            });
        }
        
        // Move form
        const moveForm = document.getElementById('fileManagerMoveForm');
        if (moveForm) {
            moveForm.addEventListener('submit', (e) => {
                e.preventDefault();
                this.moveItem(new FormData(moveForm));
            });
        }
        
        // Delete form
        const deleteForm = document.getElementById('fileManagerDeleteForm');
        if (deleteForm) {
            deleteForm.addEventListener('submit', (e) => {
                e.preventDefault();
                this.deleteItem(new FormData(deleteForm));
            });
        }
        
        // Bind close buttons
        document.querySelectorAll('.adm_notifolder_close, .adm_notifolder_cancel, .adm_totypop_close').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                const modal = btn.closest('.adm_notifolder_vidget, .adm_totypop_vidget');
                if (modal) {
                    hideFileManagerModal(modal.id);
                }
            });
        });
    }
    
    toggleView() {
        // Находим контейнер с видами
        const viewsContainer = document.querySelector('.adm_files_views');
        
        if (viewsContainer) {
            let newView;
            
            if (viewsContainer.classList.contains('active_rows_view')) {
                // Переключаем на сетку
                viewsContainer.classList.remove('active_rows_view');
                newView = 'grid';
            } else {
                // Переключаем на таблицу
                viewsContainer.classList.add('active_rows_view');
                newView = 'list';
            }
            
            // Обновляем URL с GET-параметром - это единственный источник истины
            const url = new URL(window.location);
            if (newView === 'grid') {
                // Для grid удаляем параметр view (grid - значение по умолчанию)
                url.searchParams.delete('view');
            } else {
                url.searchParams.set('view', newView);
            }
            window.history.pushState({}, '', url);
            
            // Опционально сохраняем в localStorage только для информации
            localStorage.setItem('fileManagerView', newView);
        }
    }
    
    showRenameModal(itemId, itemType, currentName) {
        const form = document.getElementById('fileManagerRenameForm');
        
        form.querySelector('[name="item_id"]').value = itemId;
        form.querySelector('[name="item_type"]').value = itemType;
        form.querySelector('[name="name"]').value = currentName;
        
        showFileManagerModal('fileManagerRenameModal');
    }
    
    showMoveModal(itemId, itemType) {
        const form = document.getElementById('fileManagerMoveForm');
        
        form.querySelector('[name="item_id"]').value = itemId;
        form.querySelector('[name="item_type"]').value = itemType;
        
        // Load folder tree
        this.loadFolderTree();
        
        showFileManagerModal('fileManagerMoveModal');
    }
    
    showDeleteModal(itemId, itemType, itemName) {
        const form = document.getElementById('fileManagerDeleteForm');
        
        form.querySelector('[name="item_id"]').value = itemId;
        form.querySelector('[name="item_type"]').value = itemType;
        document.querySelector('.delete-item-name').textContent = itemName;
        
        showFileManagerModal('fileManagerDeleteModal');
    }
    
    async loadFolderTree() {
        const container = document.getElementById('folderTreeContainer');
        if (!container) {
            console.warn('Folder tree container not found');
            return;
        }

        container.innerHTML = '<div class="text-center"><div class="spinner-border" role="status"></div></div>';

        try {
            // Add timestamp to prevent caching
            const timestamp = new Date().getTime();
            const response = await fetch(`/api/file-manager/${this.context}/${this.entityId}/folder-tree?_=${timestamp}`, {
                cache: 'no-cache',
                credentials: 'same-origin',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
                    'Accept': 'application/json'
                }
            });
            const data = await safeJsonParse(response, 'loadFolderTree');
            
            if (data.success) {
                // Convert object to array if needed
                let folders = data.folders;
                if (folders && typeof folders === 'object' && !Array.isArray(folders)) {
                    folders = Object.values(folders);
                }
                this.folderTree = folders;
                this.renderFolderTree(container);
            }
        } catch (error) {
            console.error('Error loading folder tree:', error);
            container.innerHTML = '<div class="alert alert-danger">Error loading folders</div>';
        }
    }

    /**
     * Recursively render folder item for admin widget (flat list with indentation)
     */
    renderAdminFolderItem(folder, level = 0) {
        let html = '';
        const indent = '—'.repeat(level);

        // Use translated "Root folder" for root directories, real name for others
        const isRootFolder = folder.is_root === 1 || folder.parent_id === null || folder.parent_id === 0;
        const folderName = isRootFolder
            ? (window.fileManagerTranslations?.root_folder || 'Root folder')
            : folder.name;

        const displayName = level > 0 ? `${indent} ${folderName}` : folderName;

        html += `<div class="adm_notifolder_vidget_selector_item" data-folder-id="${folder.id}" data-folder-name="${folderName}">
            ${displayName}
        </div>`;

        // Recursively render children
        if (folder.children) {
            // Convert children object to array if needed
            let children = folder.children;
            if (typeof children === 'object' && !Array.isArray(children)) {
                children = Object.values(children);
            }

            if (Array.isArray(children) && children.length > 0) {
                children.forEach(child => {
                    html += this.renderAdminFolderItem(child, level + 1);
                });
            }
        }

        return html;
    }

    renderFolderTree(container, folders = this.folderTree, level = 0) {
        let html = '';

        // Check if we're in admin context (has special widget)
        const isAdminWidget = container && container.classList.contains('adm_notifolder_vidget_selector_items');

        // Sort and render folders
        if (folders && folders.length > 0) {
            // Sort folders alphabetically
            folders.sort((a, b) => a.name.localeCompare(b.name));
            
            folders.forEach(folder => {
                // Check if has children (handle both array and object format)
                let children = folder.children;
                if (children && typeof children === 'object' && !Array.isArray(children)) {
                    children = Object.values(children);
                }
                const hasChildren = children && Array.isArray(children) && children.length > 0;

                if (isAdminWidget) {
                    // Admin widget style (flat list with recursion)
                    html += this.renderAdminFolderItem(folder, level);
                } else {
                    // Regular tree style
                    html += `
                        <div class="folder-tree-item ${hasChildren ? 'has-children' : ''}"
                             data-folder-id="${folder.id}"
                             data-folder-name="${folder.name}"
                             style="padding-left: ${(level + 1) * 20}px">
                            <i class="fas fa-folder"></i> ${folder.name}
                        </div>
                    `;

                    if (hasChildren) {
                        html += this.renderFolderTree(null, children, level + 1);
                    }
                }
            });
        }
        
        if (container) {
            container.innerHTML = html;
            
            // Bind click events based on widget type
            if (isAdminWidget) {
                // Admin widget click handler
                container.querySelectorAll('.adm_notifolder_vidget_selector_item').forEach(item => {
                    item.addEventListener('click', () => {
                        // Update selected folder display
                        const selectedName = document.getElementById('selected_folder_name');
                        if (selectedName) {
                            selectedName.textContent = item.dataset.folderName || item.textContent.trim();
                        }
                        
                        // Update hidden input
                        document.getElementById('target_folder_id').value = item.dataset.folderId;
                        
                        // Hide dropdown
                        container.style.display = 'none';
                    });
                });
                
                // Setup dropdown toggle
                const selector = document.querySelector('.adm_notifolder_vidget_selector');
                if (selector && !selector.hasAttribute('data-initialized')) {
                    selector.setAttribute('data-initialized', 'true');
                    selector.addEventListener('click', (e) => {
                        e.stopPropagation();
                        const items = container;
                        if (items.style.display === 'none' || !items.style.display) {
                            items.style.display = 'block';
                        } else {
                            items.style.display = 'none';
                        }
                    });
                    
                    // Close on outside click
                    document.addEventListener('click', (e) => {
                        if (!selector.contains(e.target) && !container.contains(e.target)) {
                            container.style.display = 'none';
                        }
                    });
                }
            } else {
                // Regular tree click handler
                container.querySelectorAll('.folder-tree-item').forEach(item => {
                    item.addEventListener('click', () => {
                        container.querySelectorAll('.folder-tree-item').forEach(i => i.classList.remove('selected'));
                        item.classList.add('selected');
                        document.getElementById('target_folder_id').value = item.dataset.folderId;
                    });
                });
            }
        }
        
        return html;
    }
    
    async uploadFiles(files) {
        // Validate files before upload
        if (!this.validateFiles(files)) {
            return;
        }

        // Check if upload modal exists, if not, we might not need it
        const modal = document.getElementById('fileManagerUploadModal');
        if (modal) {
            showFileManagerModal('fileManagerUploadModal');
        }
        
        const progressBar = modal?.querySelector('.progress-bar');
        const currentFileSpan = modal?.querySelector('.current-file');
        const uploadCountSpan = modal?.querySelector('.upload-count');
        
        const formData = new FormData();
        for (let i = 0; i < files.length; i++) {
            formData.append('files[]', files[i]);
        }
        
        const dirId = document.querySelector('[name="dir_id"]')?.value || this.currentDirId || 0;
        const type = document.querySelector('[name="type"]')?.value || 'docs';
        
        formData.append('dir_id', dirId);
        formData.append('_token', document.querySelector('meta[name="csrf-token"]').content);
        
        const total = files.length;
        
        // Update progress display if elements exist
        if (uploadCountSpan) uploadCountSpan.textContent = `0 / ${total}`;
        if (currentFileSpan) currentFileSpan.textContent = 'Uploading files...';
        
        try {
            // Unified upload using FormData for both contexts
            const url = this.context === 'project' 
                ? `/project/${this.entityId}/files/${type}/${dirId}/uploadFile`
                : `/profile/${this.entityId}/files/${type}/${dirId}/desc/uploadFile`;
            
            const response = await fetch(url, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
                },
                body: formData
            });
            
            if (response.ok) {
                if (progressBar) progressBar.style.width = '100%';
                if (uploadCountSpan) uploadCountSpan.textContent = `${total} / ${total}`;
            } else {
                throw new Error(`Upload failed with status ${response.status}`);
            }
            
            // Show success briefly if modal exists
            if (modal && modal.querySelector('.upload-results')) {
                if (modal.querySelector('.upload-progress')) {
                    modal.querySelector('.upload-progress').style.display = 'none';
                }
                modal.querySelector('.upload-results').style.display = 'block';
                if (modal.querySelector('.success-message')) {
                    modal.querySelector('.success-message').textContent = `Successfully uploaded ${total} file(s)`;
                }
            }

            // Refresh files list without page reload
            await this.refreshFilesList();

            // Close modal if open
            hideFileManagerModal('fileManagerUploadModal');

        } catch (error) {
            if (modal && modal.querySelector('.upload-progress')) {
                modal.querySelector('.upload-progress').style.display = 'none';
            }
            if (modal && modal.querySelector('.upload-results')) {
                modal.querySelector('.upload-results').style.display = 'block';
                if (modal.querySelector('.alert-danger')) {
                    modal.querySelector('.alert-danger').style.display = 'block';
                }
                if (modal.querySelector('.error-message')) {
                    modal.querySelector('.error-message').textContent = 'Error uploading files';
                }
            }
            
            // Show close button
            const closeBtn = modal?.querySelector('.adm_totypop_close');
            if (closeBtn) {
                closeBtn.style.display = 'block';
            }
        }
    }
    
    async createFolder(formData) {
        try {
            const folderName = formData.get('name');
            const dirId = formData.get('dir_id') || document.querySelector('[name="dir_id"]').value || 0;
            const type = document.querySelector('[name="type"]').value || 'all';
            
            // Создаем новую форму с правильными полями
            const submitForm = new FormData();
            submitForm.append('_token', document.querySelector('meta[name="csrf-token"]').content);
            submitForm.append('name', folderName);  // Используем 'name' вместо 'folder_name'
            submitForm.append('dir_id', dirId);  // Добавляем dir_id явно
            
            // Определяем URL в зависимости от контекста
            let url;
            if (this.context === 'project') {
                url = `/project/${this.entityId}/files/${type}/${dirId}/addfolder`;
            } else {
                url = `/profile/${this.entityId}/files/${type}/${dirId}/addfolder`;
            }

            const response = await fetch(url, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
                    'Accept': 'application/json'
                },
                body: submitForm
            });

            if (response.ok) {
                hideFileManagerModal('fileManagerFolderModal');
                showToast('success', 'Folder created successfully');
                await this.refreshFilesList();
            } else {
                const text = await response.text();
                console.error('Error response:', text);
                showToast('error', 'Error creating folder');
            }
        } catch (error) {
            console.error('Error:', error);
            showToast('error', 'Error creating folder');
        }
    }
    
    async renameItem(formData) {
        try {
            // Получаем значения из формы
            const itemType = formData.get('item_type');
            const itemId = formData.get('item_id');
            const newName = formData.get('name');
            const dirId = document.querySelector('[name="dir_id"]').value || 0;
            const type = document.querySelector('[name="type"]').value || 'all';
            
            // Создаем новую форму с правильными полями
            const submitForm = new FormData();
            submitForm.append('_token', document.querySelector('meta[name="csrf-token"]').content);
            submitForm.append('name', newName);
            submitForm.append('dir_type', itemType);
            submitForm.append('dir_id', itemId);
            
            // Определяем URL в зависимости от контекста
            let url;
            if (this.context === 'project') {
                url = `/project/${this.entityId}/files/${type}/${dirId}/renameFolder`;
            } else {
                url = `/user/${this.entityId}/files/${type}/${dirId}/renameFolder`;
            }

            const response = await fetch(url, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
                    'Accept': 'application/json'
                },
                body: submitForm
            });

            if (response.ok) {
                hideFileManagerModal('fileManagerRenameModal');
                showToast('success', 'Item renamed successfully');
                await this.refreshFilesList();
            } else {
                const text = await response.text();
                console.error('Error response:', text);
                showToast('error', 'Error renaming item');
            }
        } catch (error) {
            console.error('Error:', error);
            showToast('error', 'Error renaming item');
        }
    }
    
    async moveItem(formData) {
        try {
            const itemType = formData.get('item_type');
            const itemId = formData.get('item_id');
            const targetFolderId = formData.get('target_folder_id');
            
            // Создаем новую форму с правильными полями
            const submitForm = new FormData();
            submitForm.append('_token', document.querySelector('meta[name="csrf-token"]').content);
            submitForm.append('file_type', itemType);
            submitForm.append('file_id', itemId);
            submitForm.append('file_id_destination', targetFolderId);
            
            // Определяем URL в зависимости от контекста
            let url;
            if (this.context === 'project') {
                url = `/project/${this.entityId}/files/moveFile`;
            } else {
                url = `/profile/${this.entityId}/files/moveFile`;
            }

            const response = await fetch(url, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
                    'Accept': 'application/json'
                },
                body: submitForm
            });

            if (response.ok) {
                hideFileManagerModal('fileManagerMoveModal');
                showToast('success', 'Item moved successfully');
                await this.refreshFilesList();
            } else {
                const text = await response.text();
                console.error('Error response:', text);
                showToast('error', 'Error moving item');
            }
        } catch (error) {
            console.error('Error:', error);
            showToast('error', 'Error moving item');
        }
    }
    
    async deleteItem(formData) {
        try {
            const itemType = formData.get('item_type');
            const itemId = formData.get('item_id');
            const dirId = document.querySelector('[name="dir_id"]').value || 0;
            const type = document.querySelector('[name="type"]').value || 'all';

            // Создаем новую форму с правильными полями
            const submitForm = new FormData();
            submitForm.append('_token', document.querySelector('meta[name="csrf-token"]').content);
            submitForm.append('file_type', itemType);
            submitForm.append('file_id', itemId);

            // Единый URL для обоих контекстов - используем POST
            const url = this.context === 'project'
                ? `/project/${this.entityId}/files/${type}/${dirId}/removeFile`
                : `/profile/${this.entityId}/files/${type}/${dirId}/removeFile`;

            const response = await fetch(url, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
                    'Accept': 'application/json'
                },
                body: submitForm
            });

            if (response.ok) {
                hideFileManagerModal('fileManagerDeleteModal');
                showToast('success', 'Item deleted successfully');
                await this.refreshFilesList();
            } else {
                const text = await response.text();
                console.error('Error response:', text);
                showToast('error', 'Error deleting item');
            }
        } catch (error) {
            console.error('Error:', error);
            showToast('error', 'Error deleting item');
        }
    }

    validateFiles(files) {
        const allowedExtensions = [
            'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'rtf',
            'jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp',
            'zip', 'rar', '7z', 'tar', 'gz',
            'csv', 'xml', 'json'
        ];

        const invalidFiles = [];

        for (const file of files) {
            const extension = file.name.split('.').pop().toLowerCase();
            if (!allowedExtensions.includes(extension)) {
                invalidFiles.push(file.name);
            }
        }

        if (invalidFiles.length > 0) {
            const message = `The following file(s) have unsupported extensions: ${invalidFiles.join(', ')}. Allowed extensions: ${allowedExtensions.join(', ')}`;
            window.showToast('error', message, 8000);
            return false;
        }

        return true;
    }

    /**
     * Refresh files list without page reload
     * Used after file operations (upload, delete, rename, move, create folder)
     */
    async refreshFilesList() {
        try {
            // Reload current folder contents
            await this.loadFolderContents(this.currentDirId);

            // Also reload folder tree to show updated structure
            if (typeof this.loadFolderTree === 'function') {
                await this.loadFolderTree();
            }
        } catch (error) {
            console.error('Error refreshing files list:', error);
            showToast('error', 'Error refreshing files list');
        }
    }
}

// Window-global (используется старыми inline-скриптами в Blade-компонентах)
window.FileManager = FileManager;
window.showFileManagerModal = showFileManagerModal;
window.hideFileManagerModal = hideFileManagerModal;
window.closeFileManagerModal = closeFileManagerModal;

// Авто-инициализация только при наличии маркера компонента на странице
function autoInit() {
    if (document.querySelector('.file-manager-component')) {
        new FileManager();
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoInit);
} else {
    autoInit();
}